import {Moment} from 'moment';

const DAY = 'day' as const;
const MONTH = 'month' as const;

function getDiff(startDate: Moment, endDate: Moment) {
  const startDateMinusOne = startDate.clone().subtract(1, 'day');
  const startDateOfMonthMinusOne = startDateMinusOne.date();
  const startDateOfMonth = startDate.date();
  const endDateOfMonth = endDate.date();
  if (startDateOfMonth === 1 && endDateOfMonth === endDate.clone().endOf('month').date()) {
    const diff = Math.round(endDate.diff(startDateMinusOne, 'months', true));
    return {diff: diff, diffUnit: MONTH, isEndOfMonth: true};
  } else if (startDateOfMonthMinusOne === endDateOfMonth) {
    const diff = Math.round(endDate.diff(startDateMinusOne, 'months', true));
    return {diff: diff, diffUnit: MONTH, isEndOfMonth: false};
  } else {
    const diff = endDate.diff(startDateMinusOne, DAY);
    return {diff: diff, diffUnit: DAY, isEndOfMonth: false};
  }
}

/**
 * Given a pair of start and end dates, get the next or previous date range. The logic applies
 * heuristics to decide what is the best fit. E.g. if the passed-in range represents a whole month,
 * then the returned range will also be a whole month, even though the exact number of days in the ranges
 * may be different.
 * @param operation "add" or "subtract"
 * @param startDate start date, inclusive
 * @param endDate end date, inclusive NOTE the difference with our norm. This is because the dates
 * in the date range widget are inclusive on both ends (makes sense for UI)
 * @param maxDate max date used in case the operation is add, the returned end date will be at most maxDate
 * @returns the next or previous date range, both start and end are inclusive
 */
export function addSubtractDayInterval(
  operation: 'add' | 'subtract',
  startDate: Moment,
  endDate: Moment,
  maxDate: Moment
) {
  if (!startDate || !endDate) {
    return;
  }
  const {diff, diffUnit, isEndOfMonth} = getDiff(startDate, endDate);
  if (operation === 'subtract') {
    const adjustedStartDate = startDate.clone().subtract(diff, diffUnit);
    const adjustedEndDate = endDate.clone().subtract(diff, diffUnit);
    return {
      startDate: adjustedStartDate,
      endDate: isEndOfMonth
        ? // Need start-of-day so that the new end date and the old end date will have the same time.
          // Without it, the end-of-month will be literally that (23:59:59), and moment.js being very helpful
          // will make the time UTC-based, and that will make the date part advance one more day when it gets
          // to the BE
          adjustedEndDate.endOf(MONTH).startOf(DAY)
        : adjustedEndDate,
    };
  } else if (operation === 'add') {
    const maxDiff = maxDate.diff(endDate, DAY);
    if (maxDiff <= 0) {
      return;
    }
    const addedEndDate = endDate.clone().add(diff, diffUnit);
    const desiredEndDate = isEndOfMonth ? addedEndDate.endOf(MONTH) : addedEndDate;
    let adjustedStartDate;
    let adjustedEndDate;
    if (desiredEndDate.isAfter(maxDate)) {
      adjustedStartDate = startDate.clone().add(maxDiff, DAY);
      adjustedEndDate = endDate.clone().add(maxDiff, DAY);
    } else {
      adjustedStartDate = startDate.clone().add(diff, diffUnit);
      adjustedEndDate = desiredEndDate;
    }
    return {
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
    };
  }
  // Fall through, return undefined.
}

/**
 * Same as above but endDate both in and out are exclusive
 */
export function addSubtractDayIntervalEndDateExclusive(operation, startDate, endDate, maxDate) {
  const endDateIncl = endDate.clone().subtract(1, 'day');
  const res = addSubtractDayInterval(operation, startDate, endDateIncl, maxDate);
  if (res) {
    const {startDate: newStartDate, endDate: newEndDate} = res;

    return {
      startDate: newStartDate,
      endDate: newEndDate.add(1, 'day'),
    };
  }

  throw new Error(
    "addSubtractDayIntervalEndDateExclusive function doesn't have required argument(s)"
  );
}
